import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { MovieData } from "@/components/SwipeCard";

/**
 * Hook for syncing user data between localStorage (guest) and database (authed).
 * When logged in, DB is the source of truth. localStorage is kept as a cache.
 */
export const useUserData = () => {
  const { user } = useAuth();

  // ─── Bookmarks ───
  const getBookmarks = useCallback(async (): Promise<MovieData[]> => {
    if (!user) {
      const raw = localStorage.getItem("ayeris_bookmarks");
      return raw ? JSON.parse(raw) : [];
    }
    const { data } = await (supabase
      .from("user_bookmarks") as any)
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    return (data || []).map(rowToMovie);
  }, [user]);

  const addBookmark = useCallback(async (movie: MovieData, mediaType = "movie") => {
    if (!user) {
      const raw = localStorage.getItem("ayeris_bookmarks");
      const existing: MovieData[] = raw ? JSON.parse(raw) : [];
      if (!existing.find((b) => b.id === movie.id)) {
        existing.unshift(movie);
        localStorage.setItem("ayeris_bookmarks", JSON.stringify(existing));
      }
      return;
    }
    await (supabase.from("user_bookmarks") as any).upsert({
      user_id: user.id,
      tmdb_id: movie.id,
      media_type: mediaType,
      title: movie.title,
      poster: movie.poster,
      year: movie.year,
      runtime: movie.runtime,
      rating: movie.rating,
      genres: movie.genres || [],
      providers: movie.providers || [],
    }, { onConflict: "user_id,tmdb_id" });
  }, [user]);

  const removeBookmark = useCallback(async (tmdbId: number) => {
    if (!user) {
      const raw = localStorage.getItem("ayeris_bookmarks");
      const existing: MovieData[] = raw ? JSON.parse(raw) : [];
      localStorage.setItem("ayeris_bookmarks", JSON.stringify(existing.filter((b) => b.id !== tmdbId)));
      return;
    }
    await (supabase.from("user_bookmarks") as any).delete().eq("user_id", user.id).eq("tmdb_id", tmdbId);
  }, [user]);

  // ─── Watch History ───
  const getWatchHistory = useCallback(async () => {
    if (!user) {
      const raw = localStorage.getItem("ayeris_watch_history");
      return raw ? JSON.parse(raw) : [];
    }
    const { data } = await (supabase
      .from("user_watch_history") as any)
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(200);
    return (data || []).map((r: any) => ({ ...rowToMovie(r), seenRating: r.seen_rating, picked: r.picked, media_type: r.media_type || "movie" }));
  }, [user]);

  const addToWatchHistory = useCallback(async (movie: MovieData, seenRating?: string, picked?: boolean, mediaType = "movie") => {
    if (!user) {
      const raw = localStorage.getItem("ayeris_watch_history");
      const history = raw ? JSON.parse(raw) : [];
      const existing = history.findIndex((h: any) => h.id === movie.id);
      if (existing >= 0) {
        if (seenRating) history[existing].seenRating = seenRating;
        if (picked) history[existing].picked = picked;
      } else {
        history.unshift({ ...movie, seenRating, picked, media_type: mediaType });
      }
      localStorage.setItem("ayeris_watch_history", JSON.stringify(history.slice(0, 200)));
      return;
    }
    await (supabase.from("user_watch_history") as any).upsert({
      user_id: user.id,
      tmdb_id: movie.id,
      title: movie.title,
      poster: movie.poster,
      year: movie.year,
      runtime: movie.runtime,
      rating: movie.rating,
      genres: movie.genres || [],
      seen_rating: seenRating || null,
      picked: picked || false,
      media_type: mediaType,
    }, { onConflict: "user_id,tmdb_id" });
  }, [user]);

  const removeFromWatchHistory = useCallback(async (tmdbId: number) => {
    if (!user) {
      const raw = localStorage.getItem("ayeris_watch_history");
      const history = raw ? JSON.parse(raw) : [];
      localStorage.setItem("ayeris_watch_history", JSON.stringify(history.filter((h: any) => h.id !== tmdbId)));
      return;
    }
    await (supabase.from("user_watch_history") as any).delete().eq("user_id", user.id).eq("tmdb_id", tmdbId);
  }, [user]);

  const getWatchHistoryRating = useCallback(async (tmdbId: number): Promise<string | null> => {
    if (!user) {
      const raw = localStorage.getItem("ayeris_watch_history");
      const history = raw ? JSON.parse(raw) : [];
      const item = history.find((h: any) => h.id === tmdbId);
      return item?.seenRating || null;
    }
    const { data } = await (supabase
      .from("user_watch_history") as any)
      .select("seen_rating")
      .eq("user_id", user.id)
      .eq("tmdb_id", tmdbId)
      .maybeSingle();
    return data?.seen_rating || null;
  }, [user]);

  // ─── Review Pending ───
  const getReviewPending = useCallback(async (): Promise<MovieData[]> => {
    if (!user) {
      const raw = localStorage.getItem("ayeris_review_pending");
      return raw ? JSON.parse(raw) : [];
    }
    const { data } = await (supabase
      .from("user_review_pending") as any)
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    return (data || []).map(rowToMovie);
  }, [user]);

  const addToReviewPending = useCallback(async (movie: MovieData, mediaType = "movie") => {
    if (!user) {
      const raw = localStorage.getItem("ayeris_review_pending");
      const existing: any[] = raw ? JSON.parse(raw) : [];
      if (!existing.find((r) => r.id === movie.id)) {
        existing.unshift({ ...movie, media_type: mediaType });
        localStorage.setItem("ayeris_review_pending", JSON.stringify(existing));
      }
      return;
    }
    await (supabase.from("user_review_pending") as any).upsert({
      user_id: user.id,
      tmdb_id: movie.id,
      media_type: mediaType,
      title: movie.title,
      poster: movie.poster,
      year: movie.year,
      runtime: movie.runtime,
      rating: movie.rating,
      genres: movie.genres || [],
      providers: movie.providers || [],
    }, { onConflict: "user_id,tmdb_id" });
  }, [user]);

  const removeFromReviewPending = useCallback(async (tmdbId: number) => {
    if (!user) {
      const raw = localStorage.getItem("ayeris_review_pending");
      const existing: any[] = raw ? JSON.parse(raw) : [];
      localStorage.setItem("ayeris_review_pending", JSON.stringify(existing.filter((r) => r.id !== tmdbId)));
      return;
    }
    await (supabase.from("user_review_pending") as any).delete().eq("user_id", user.id).eq("tmdb_id", tmdbId);
  }, [user]);

  const reviewMovie = useCallback(async (movie: MovieData, rating: "loved" | "pretty_good" | "ok" | "not_great" | "hated", mediaType = "movie") => {
    await removeFromReviewPending(movie.id);
    await addToWatchHistory(movie, rating, false, mediaType);
  }, [removeFromReviewPending, addToWatchHistory]);

  // ─── Currently Watching ───
  const getCurrentlyWatching = useCallback(async () => {
    if (!user) {
      const raw = localStorage.getItem("ayeris_currently_watching");
      return raw ? JSON.parse(raw) : [];
    }
    const { data } = await (supabase
      .from("user_currently_watching") as any)
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    return (data || []).map((r: any) => ({
      id: r.tmdb_id,
      title: r.title,
      poster: r.poster || "",
      year: r.year || 0,
      media_type: r.media_type || "tv",
    }));
  }, [user]);

  const addCurrentlyWatching = useCallback(async (show: { id: number; title: string; poster?: string; year?: number; media_type?: string }) => {
    if (!user) {
      const raw = localStorage.getItem("ayeris_currently_watching");
      const existing: any[] = raw ? JSON.parse(raw) : [];
      if (!existing.find((s) => s.id === show.id)) {
        existing.unshift({ id: show.id, title: show.title, poster: show.poster || "", year: show.year || 0, media_type: show.media_type || "tv" });
        localStorage.setItem("ayeris_currently_watching", JSON.stringify(existing));
      }
      return;
    }
    await (supabase.from("user_currently_watching") as any).upsert({
      user_id: user.id,
      tmdb_id: show.id,
      media_type: show.media_type || "tv",
      title: show.title,
      poster: show.poster || "",
      year: show.year || 0,
    }, { onConflict: "user_id,tmdb_id" });
  }, [user]);

  const removeCurrentlyWatching = useCallback(async (tmdbId: number) => {
    if (!user) {
      const raw = localStorage.getItem("ayeris_currently_watching");
      const existing: any[] = raw ? JSON.parse(raw) : [];
      localStorage.setItem("ayeris_currently_watching", JSON.stringify(existing.filter((s) => s.id !== tmdbId)));
      return;
    }
    await (supabase.from("user_currently_watching") as any).delete().eq("user_id", user.id).eq("tmdb_id", tmdbId);
  }, [user]);

  // ─── Favorites ───
  const getFavorites = useCallback(async () => {
    if (!user) {
      const raw = localStorage.getItem("ayeris_favorites");
      return raw ? JSON.parse(raw) : [];
    }
    const { data } = await (supabase
      .from("user_favorites") as any)
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    return (data || []).map((r: any) => ({
      id: r.tmdb_id,
      title: r.title,
      poster: r.poster,
      year: r.year,
      media_type: r.media_type,
    }));
  }, [user]);

  const setFavorites = useCallback(async (favorites: any[], mediaType = "movie") => {
    if (!user) {
      localStorage.setItem("ayeris_favorites", JSON.stringify(favorites));
      return;
    }
    // Replace all favorites for this user
    await (supabase.from("user_favorites") as any).delete().eq("user_id", user.id);
    if (favorites.length > 0) {
      await (supabase.from("user_favorites") as any).insert(
        favorites.map((f: any) => ({
          user_id: user.id,
          tmdb_id: f.id,
          media_type: f.media_type || mediaType,
          title: f.title || f.name || "",
          poster: f.poster || (f.poster_path ? `https://image.tmdb.org/t/p/w500${f.poster_path}` : ""),
          year: f.year || 0,
        }))
      );
    }
  }, [user]);

  // ─── Migrate localStorage to DB on first login ───
  const migrateLocalToDb = useCallback(async () => {
    if (!user) return;

    // Bookmarks
    const localBookmarks = localStorage.getItem("ayeris_bookmarks");
    if (localBookmarks) {
      const bookmarks: MovieData[] = JSON.parse(localBookmarks);
      for (const movie of bookmarks) {
        await addBookmark(movie);
      }
    }

    // Watch history
    const localHistory = localStorage.getItem("ayeris_watch_history");
    if (localHistory) {
      const history = JSON.parse(localHistory);
      for (const item of history.slice(0, 50)) {
        await addToWatchHistory(item, item.seenRating, item.picked);
      }
    }

    // Favorites
    const localFavs = localStorage.getItem("ayeris_favorites");
    if (localFavs) {
      const favs = JSON.parse(localFavs);
      await setFavorites(favs);
    }
  }, [user, addBookmark, addToWatchHistory, setFavorites]);

  // ─── Hidden Titles (Don't Recommend Again) ───
  const getHiddenTitles = useCallback(async (): Promise<(MovieData & { media_type?: string })[]> => {
    if (!user) {
      const raw = localStorage.getItem("ayeris_hidden_titles");
      return raw ? JSON.parse(raw) : [];
    }
    const { data } = await (supabase
      .from("user_hidden_titles") as any)
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    return (data || []).map((r: any) => ({
      id: r.tmdb_id,
      title: r.title,
      synopsis: "",
      poster: r.poster || "",
      year: r.year || 0,
      runtime: 0,
      rating: 0,
      genres: [],
      media_type: r.media_type || "movie",
    }));
  }, [user]);

  const addHiddenTitle = useCallback(async (movie: MovieData, mediaType = "movie") => {
    if (!user) {
      const raw = localStorage.getItem("ayeris_hidden_titles");
      const existing: any[] = raw ? JSON.parse(raw) : [];
      if (!existing.find((h: any) => h.id === movie.id)) {
        existing.unshift({ id: movie.id, title: movie.title, poster: movie.poster, year: movie.year, media_type: mediaType });
        localStorage.setItem("ayeris_hidden_titles", JSON.stringify(existing));
      }
      return;
    }
    await (supabase.from("user_hidden_titles") as any).upsert({
      user_id: user.id,
      tmdb_id: movie.id,
      media_type: mediaType,
      title: movie.title,
      poster: movie.poster || "",
      year: movie.year || 0,
    }, { onConflict: "user_id,tmdb_id" });
  }, [user]);

  const removeHiddenTitle = useCallback(async (tmdbId: number) => {
    if (!user) {
      const raw = localStorage.getItem("ayeris_hidden_titles");
      const existing: any[] = raw ? JSON.parse(raw) : [];
      localStorage.setItem("ayeris_hidden_titles", JSON.stringify(existing.filter((h: any) => h.id !== tmdbId)));
      return;
    }
    await (supabase.from("user_hidden_titles") as any).delete().eq("user_id", user.id).eq("tmdb_id", tmdbId);
  }, [user]);

  return {
    getBookmarks, addBookmark, removeBookmark,
    getWatchHistory, addToWatchHistory, removeFromWatchHistory, getWatchHistoryRating,
    getReviewPending, addToReviewPending, removeFromReviewPending, reviewMovie,
    getFavorites, setFavorites,
    getCurrentlyWatching, addCurrentlyWatching, removeCurrentlyWatching,
    getHiddenTitles, addHiddenTitle, removeHiddenTitle,
    migrateLocalToDb,
  };
};

function rowToMovie(r: any): MovieData & { media_type?: string } {
  return {
    id: r.tmdb_id,
    title: r.title,
    synopsis: "",
    poster: r.poster || "",
    year: r.year || 0,
    runtime: r.runtime || 0,
    rating: r.rating || 0,
    genres: r.genres || [],
    providers: r.providers || [],
    media_type: r.media_type || "movie",
  };
}
